import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  extractPdfText,
  txtFilename,
  validateFileSize,
  type ExtractProgress,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'

const UTILITY = getUtilityDefinition('pdfToText').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File; progress: ExtractProgress }
  | {
      kind: 'success'
      file: File
      text: string
      objectUrl: string
      filename: string
      pageCount: number
      charCount: number
      wordCount: number
      warning: 'too_little_text' | null
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          inputRef.current?.click()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(event.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files)
          event.currentTarget.value = ''
        }}
      />
      <div className="text-4xl" aria-hidden>
        ¶
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          {t.common.localBadge}
        </p>
      </div>
    </div>
  )
}

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        ×
      </button>
    </div>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function ProgressPanel({ progress }: { progress: ExtractProgress }) {
  const t = useT()
  const total = progress.total || 1
  const percent = Math.round((progress.current / total) * 100)

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">{t.pdfToText.progressTitle}</span>
          <span className="text-muted-foreground">
            {format(t.pdfToText.progressPages, {
              current: progress.current,
              total: progress.total,
            })}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="pt-5">
        <p className="mb-2 text-sm font-semibold text-destructive">{t.common.errorTitle}</p>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

interface ResultPanelProps {
  phase: Extract<Phase, { kind: 'success' }>
  onReset: () => void
}

function ResultPanel({ phase, onReset }: ResultPanelProps) {
  const t = useT()
  const preview = phase.text.length > 4000 ? `${phase.text.slice(0, 4000)}…` : phase.text

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{t.common.done}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {format(t.pdfToText.resultStats, {
                pages: phase.pageCount,
                words: phase.wordCount,
                chars: phase.charCount,
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <a href={phase.objectUrl} download={phase.filename}>
                {t.pdfToText.downloadTxt}
              </a>
            </Button>
            <Button type="button" variant="outline" onClick={onReset}>
              {t.pdfToText.clear}
            </Button>
          </div>
        </div>

        {phase.warning === 'too_little_text' && (
          <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
            {t.pdfToText.tooLittleTextWarning}
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium text-foreground">{t.pdfToText.previewTitle}</p>
          <pre className="max-h-96 overflow-auto rounded-lg border bg-background p-4 text-sm leading-6 text-foreground whitespace-pre-wrap">
            {preview}
          </pre>
        </div>
      </CardContent>
    </Card>
  )
}

function classifyError(error: unknown, t: Translations): { code: string; message: string } {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error)
  if (/password|encrypted|PasswordException/i.test(text)) {
    return { code: 'encrypted', message: t.pdfToText.encrypted }
  }
  if (/no_text_layer/i.test(text)) {
    return { code: 'no_text_layer', message: t.pdfToText.noTextLayer }
  }
  if (/InvalidPDF|invalid|damaged|corrupt|parse/i.test(text)) {
    return { code: 'invalid_pdf', message: t.pdfToText.invalidPdf }
  }
  return { code: 'extract_error', message: t.pdfToText.extractError }
}

export function PdfVTextForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)
  const resultUrlRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  const isLoading = phase.kind === 'loading'
  const currentFile =
    phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
      ? phase.file
      : null

  function revokeSuccessUrl() {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current)
      resultUrlRef.current = null
    }
  }

  function handleFile(file: File) {
    revokeSuccessUrl()
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }
    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    abortRef.current?.abort()
    revokeSuccessUrl()
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const file = phase.file
    const controller = new AbortController()
    abortRef.current = controller
    const startMs = Date.now()

    setPhase({ kind: 'loading', file, progress: { current: 0, total: 0 } })
    toolInvoke(UTILITY, { input_bytes: file.size })

    try {
      const result = await extractPdfText({
        file,
        signal: controller.signal,
        onProgress: (progress) => {
          setPhase((current) => (current.kind === 'loading' ? { ...current, progress } : current))
        },
      })
      const blob = new Blob([result.text], { type: 'text/plain;charset=utf-8' })
      const objectUrl = URL.createObjectURL(blob)
      const durationMs = Date.now() - startMs
      resultUrlRef.current = objectUrl

      toolSuccess(UTILITY, durationMs, {
        input_bytes: file.size,
        output_bytes: blob.size,
        pages: result.pageCount,
        chars: result.charCount,
        warning: result.warning,
      })

      setPhase({
        kind: 'success',
        file,
        text: result.text,
        objectUrl,
        filename: txtFilename(file.name),
        pageCount: result.pageCount,
        charCount: result.charCount,
        wordCount: result.wordCount,
        warning: result.warning,
      })
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      const { code, message } = classifyError(error, t)
      toolError(UTILITY, code, { input_bytes: file.size })
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return <ResultPanel phase={phase} onReset={handleClear} />
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {t.pdfToText.noOcrNotice}
      </div>

      {phase.kind === 'loading' && <ProgressPanel progress={phase.progress} />}
      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.pdfToText.loading}
          </>
        ) : (
          t.pdfToText.submit
        )}
      </Button>
    </div>
  )
}
